import { getEnvironment } from "@/lib/environment";
import { createContext, useContext, useState } from "react";

const DEBUG_SETTINGS_LOCAL_STORAGE_KEY = "99r-debug-settings";

type DebugSettings = {
  overlay: boolean | undefined;
};

const DEFAULT: DebugSettings = {
  overlay: getEnvironment() === "development",
};

const DebugSettingsContext = createContext<{
  settings: DebugSettings;
  setSettings: React.Dispatch<React.SetStateAction<DebugSettings>>;
}>({
  settings: DEFAULT,
  setSettings: () => {
    throw new Error(
      "setSettings() must be used within a DebugSettingsProvider",
    );
  },
});

export const DebugSettingsProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [settings, setSettings] = useState(getLocalSettings);

  return (
    <DebugSettingsContext.Provider value={{ settings, setSettings }}>
      {children}
    </DebugSettingsContext.Provider>
  );
};

const getLocalSettings = (): DebugSettings => {
  const local = localStorage.getItem(DEBUG_SETTINGS_LOCAL_STORAGE_KEY);
  const settings: DebugSettings = JSON.parse(local || "{}");
  const localSettings = {
    ...DEFAULT,
    ...settings,
  };
  return localSettings;
};

export const useDebugSettings = () => {
  const { settings, setSettings } = useContext(DebugSettingsContext);

  const updateSettings = (settings: Partial<DebugSettings>) => {
    const local = getLocalSettings();
    const newSettings = {
      ...local,
      ...settings,
    };
    localStorage.setItem(
      DEBUG_SETTINGS_LOCAL_STORAGE_KEY,
      JSON.stringify(newSettings),
    );
    setSettings(newSettings);
  };

  return {
    overlay: settings.overlay,
    setOverlay: (overlay: boolean | undefined) => {
      updateSettings({
        overlay,
      });
    },
  };
};
